import { useState, useCallback, useMemo } from "react";
import { findNode, makeService, makeResource, uid } from "../utils";

function mergeServicesForLocation(newServices, locationId, services, serviceName) {
  for (const templateService of services) {
    const name = serviceName(templateService);
    if (!name) continue;
    const existingIndex = newServices.findIndex((service) => service.locationId === locationId && service.name === name);
    if (existingIndex >= 0) {
      newServices[existingIndex] = { ...newServices[existingIndex], available: templateService.available, availType: templateService.availType, autoProv: templateService.autoProv, sso: templateService.sso, assignable: templateService.assignable };
    } else {
      newServices.push(makeService({ id: uid(), name, locId: locationId, available: templateService.available, availType: templateService.availType, autoProv: templateService.autoProv, sso: templateService.sso, assignable: templateService.assignable }));
    }
  }
}

function mergeResourcesForLocation(newResources, locationId, resources, resourceName) {
  for (const templateResource of resources) {
    const name = resourceName(templateResource);
    if (!name) continue;
    const existingIndex = newResources.findIndex((resource) => resource.locationId === locationId && resource.name === name);
    if (existingIndex >= 0) {
      newResources[existingIndex] = { ...newResources[existingIndex], active: templateResource.active, autoAssign: templateResource.autoAssign, hidden: templateResource.hidden };
    } else {
      newResources.push(makeResource(uid(), name, "", locationId, templateResource.active, templateResource.autoAssign, templateResource.hidden));
    }
  }
}

function applyTemplateLocally(wizardSelectedTemplate, wizardLeafs, wizardMode, services, resources, serviceName, resourceName) {
  let newServices = [...services];
  let newResources = [...resources];
  for (const locationId of wizardLeafs) {
    if (wizardMode === "overwrite") {
      newServices = newServices.filter((service) => service.locationId !== locationId);
      newResources = newResources.filter((resource) => resource.locationId !== locationId);
    }
    mergeServicesForLocation(newServices, locationId, wizardSelectedTemplate.services, serviceName);
    mergeResourcesForLocation(newResources, locationId, wizardSelectedTemplate.resources, resourceName);
  }
  return { newServices, newResources };
}

/**
 * @param {Array} [locationsTree] - Location tree (e.g. from API).
 * @param {{ api: object, servicesRef: Array, resourcesRef: Array, refetchConfig: () => void }} [apiOpts] - When provided, applyWizard calls API to create/update associations per location.
 */
export function useWizardState({ services, setServices, resources, setResources, templates, getConfig, notify, setTab, locationsTree, apiOpts }) {
  const locations = useMemo(() => locationsTree ?? [], [locationsTree]);

  const [wizardStep, setWizardStep] = useState(0);
  const [wizardTemplateId, setWizardTemplateId] = useState(null);
  const [wizardChecked, setWizardChecked] = useState(new Set());
  const [wizardExpanded, setWizardExpanded] = useState(new Set(["ga", "tn"]));
  const [wizardMode, setWizardMode] = useState("merge");
  const [wizardSearch, setWizardSearch] = useState("");
  const [wizardDone, setWizardDone] = useState(false);
  const [wizardApplyErrors, setWizardApplyErrors] = useState([]);
  const [wizardUnresolvedServiceIds, setWizardUnresolvedServiceIds] = useState([]);
  const [wizardUnresolvedResourceIds, setWizardUnresolvedResourceIds] = useState([]);

  const wizardReset = useCallback(() => {
    setWizardStep(0);
    setWizardTemplateId(null);
    setWizardChecked(new Set());
    setWizardMode("merge");
    setWizardSearch("");
    setWizardDone(false);
    setWizardApplyErrors([]);
    setWizardUnresolvedServiceIds([]);
    setWizardUnresolvedResourceIds([]);
  }, []);

  const wizardCheck = useCallback((id, descendants) => {
    setWizardChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) descendants.forEach((descendant) => next.delete(descendant));
      else descendants.forEach((descendant) => next.add(descendant));
      return next;
    });
  }, []);

  const getLeafs = useCallback((checked) => {
    return [...checked].filter((id) => {
      const node = findNode(locations, id);
      return node && (!node.children?.length || !node.children.some((child) => checked.has(child.id)));
    });
  }, [locations]);

  const wizardSelectedTemplate = templates.find((template) => template.id === wizardTemplateId);
  const wizardLeafs = useMemo(() => getLeafs(wizardChecked), [wizardChecked, getLeafs]);

  const servicesRef = useMemo(() => apiOpts?.servicesRef ?? [], [apiOpts?.servicesRef]);
  const resourcesRef = useMemo(() => apiOpts?.resourcesRef ?? [], [apiOpts?.resourcesRef]);
  const serviceName = useCallback(
    (templateService) => {
      if (templateService.serviceId == null) return null;
      const ref = servicesRef.find((service) => service.id === templateService.serviceId);
      return ref?.displayName ?? ref?.name ?? String(templateService.serviceId);
    },
    [servicesRef]
  );
  const resourceName = useCallback(
    (templateResource) => {
      if (templateResource.resourceId == null) return null;
      const ref = resourcesRef.find((resource) => resource.id === templateResource.resourceId);
      return ref?.name ?? String(templateResource.resourceId);
    },
    [resourcesRef]
  );

  const wizardOverlap = useMemo(() => {
    if (!wizardSelectedTemplate) return false;
    const templateServiceNames = wizardSelectedTemplate.services.map(serviceName).filter(Boolean);
    const templateResourceNames = wizardSelectedTemplate.resources.map(resourceName).filter(Boolean);
    return wizardLeafs.some((locationId) => {
      const locationConfig = getConfig(locationId);
      if (!locationConfig) return false;
      const directServiceNames = new Set((locationConfig.services ?? []).filter((d) => d.origin === "direct").map((d) => d.name));
      const directResourceNames = new Set((locationConfig.resources ?? []).filter((d) => d.origin === "direct").map((d) => d.name));
      return templateServiceNames.some((name) => directServiceNames.has(name)) || templateResourceNames.some((name) => directResourceNames.has(name));
    });
  }, [wizardSelectedTemplate, wizardLeafs, getConfig, serviceName, resourceName]);

  const wizardNext = useCallback(() => {
    if (wizardStep === 1 && !wizardOverlap) setWizardStep(3);
    else setWizardStep((step) => step + 1);
  }, [wizardStep, wizardOverlap]);

  const wizardPrev = useCallback(() => {
    if (wizardStep === 3 && !wizardOverlap) setWizardStep(1);
    else setWizardStep((step) => step - 1);
  }, [wizardStep, wizardOverlap]);

  const wizardCanGo = wizardStep === 0 ? !!wizardTemplateId : (wizardStep !== 1 || wizardChecked.size > 0);

  const applyWizard = useCallback(async () => {
    if (!wizardSelectedTemplate) return;
    if (apiOpts?.api && apiOpts?.refetchConfig) {
      const { api, refetchConfig } = apiOpts;
      try {
        const result = await api.applyTemplate(wizardSelectedTemplate.id, {
          locationIds: wizardLeafs,
          mode: wizardMode,
        });
        const applied = result?.applied ?? 0;
        const errors = result?.errors ?? [];
        const unresolvedServiceIds = result?.unresolvedServiceIds ?? [];
        const unresolvedResourceIds = result?.unresolvedResourceIds ?? [];
        setWizardApplyErrors(errors);
        setWizardUnresolvedServiceIds(unresolvedServiceIds);
        setWizardUnresolvedResourceIds(unresolvedResourceIds);
        setWizardDone(true);
        if (errors.length) {
          notify(`Applied to ${applied} location(s); ${errors.length} failed`, "warning");
        } else if (unresolvedServiceIds.length || unresolvedResourceIds.length) {
          notify(
            `Applied to ${applied} location(s). Some template entries were not found in the catalog and were skipped.`,
            "warning"
          );
        } else {
          notify(`Applied to ${applied} location(s)`);
        }
        refetchConfig();
      } catch (err) {
        notify(err?.message || "Apply failed", "error");
      }
      return;
    }
    const { newServices, newResources } = applyTemplateLocally(wizardSelectedTemplate, wizardLeafs, wizardMode, services, resources, serviceName, resourceName);
    setServices(newServices);
    setResources(newResources);
    setWizardApplyErrors([]);
    setWizardDone(true);
    notify(`Applied to ${wizardLeafs.length} location(s)`);
  }, [
    wizardSelectedTemplate,
    wizardLeafs,
    wizardMode,
    services,
    resources,
    setServices,
    setResources,
    notify,
    apiOpts,
    serviceName,
    resourceName,
  ]);

  const wizardStartApply = useCallback((templateId) => {
    setWizardTemplateId(templateId);
    setTab("wizard");
    setWizardStep(1);
    setWizardChecked(new Set());
    setWizardDone(false);
    setWizardApplyErrors([]);
    setWizardUnresolvedServiceIds([]);
    setWizardUnresolvedResourceIds([]);
  }, [setTab]);

  return {
    wizardStep,
    setWizardStep,
    wizardTemplateId,
    setWizardTemplateId,
    wizardChecked,
    wizardExpanded,
    setWizardExpanded,
    wizardMode,
    setWizardMode,
    wizardSearch,
    setWizardSearch,
    wizardDone,
    wizardApplyErrors,
    wizardUnresolvedResourceIds,
    wizardUnresolvedServiceIds,
    wizardReset,
    wizardCheck,
    getLeafs,
    wizardSelectedTemplate,
    wizardLeafs,
    wizardOverlap,
    wizardNext,
    wizardPrev,
    wizardCanGo,
    applyWizard,
    wizardStartApply,
  };
}
